import type { RegistryEntry, UiOption } from './types';
import { googleConfigs } from './configs/google';
import { klingConfigs } from './configs/kling';
import { grokConfigs } from './configs/grok';
import { viduConfigs } from './configs/vidu';
import { volcengineConfigs } from './configs/volcengine';
import { alibabaConfigs } from './configs/alibaba';
import { pixverseConfigs } from './configs/pixverse';
import { hailuoConfigs } from './configs/hailuo';
import { elevenlabsConfigs } from './configs/elevenlabs';
import { heygenConfigs } from './configs/heygen';
import { upscalerConfigs } from './configs/upscaler';
import { nvidiaConfigs } from './configs/nvidia';
import { baiduVodConfigs } from './configs/baidu-vod';
import { fluxConfigs } from './configs/flux';

export type {
    UiOption,
    UiOptionSwitch,
    UiOptionSlider,
    UiOptionNumber,
    UiOptionSelect,
    UiOptionInput,
} from './types';

/**
 * 各厂商/模型族的 UI 选项配置，按文件拆分（见 ./configs）。
 * 新增模型时，请在对应厂商的 config 文件里添加，并在此处合并。
 */
const registry: Record<string, RegistryEntry> = {
    ...googleConfigs,
    ...klingConfigs,
    ...grokConfigs,
    ...viduConfigs,
    ...volcengineConfigs,
    ...alibabaConfigs,
    ...pixverseConfigs,
    ...hailuoConfigs,
    ...elevenlabsConfigs,
    ...heygenConfigs,
    ...upscalerConfigs,
    ...nvidiaConfigs,
    ...baiduVodConfigs,
    ...fluxConfigs,
};

export const getUiOptionsForModel = (modelId: string): UiOption[] =>
    registry[modelId]?.options ?? [];

/**
 * Returns { key: default } for every option with group === 'hidden'.
 * Used to pre-fill modelUi so hidden defaults are always present.
 */
export const getHiddenDefaults = (modelId: string): Record<string, any> => {
    const entry = registry[modelId];
    if (!entry) return {};
    const defaults: Record<string, any> = {};
    for (const opt of entry.options) {
        if (opt.group === 'hidden' && 'default' in opt) {
            defaults[opt.key] = (opt as any).default;
        }
    }
    return defaults;
};

export const buildProviderOptions = (
    modelId: string,
    ui: Record<string, any>
): Record<string, Record<string, any>> => {
    const entry = registry[modelId];
    if (!entry) return {};
    const hiddenDefaults = getHiddenDefaults(modelId);
    const merged = Object.keys(hiddenDefaults).length
        ? { ...hiddenDefaults, ...ui }
        : ui;
    return entry.toProviderOptions(merged);
};
